import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import apiService from "../../services/api";
import { validatePassword } from "../../utils/validation";
import { getErrorMessage } from "../../utils/errorMessages";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const PasswordSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginJobSeeker, loginCompanyUser } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const email = searchParams.get("email") || "";
  const flow = searchParams.get("flow") || "job_seeker"; // Default to job_seeker for backward compatibility
  const isChangePasswordPage =
    location.pathname === "/candidate/change-password" ||
    location.pathname === "/company/change-password";
  const isCompanyChangePassword =
    location.pathname === "/company/change-password";
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field-specific error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    setError("");
    setSuccess("");
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.currentPassword) {
      errors.currentPassword = "Current password is required";
    }

    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      errors.newPassword = passwordValidation.message;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      if (isChangePasswordPage) {
        const response = isCompanyChangePassword
          ? await apiService.changeCompanyUserPassword(
              formData.currentPassword,
              formData.newPassword,
              formData.confirmPassword,
            )
          : await apiService.changeJobSeekerPassword(
              formData.currentPassword,
              formData.newPassword,
              formData.confirmPassword,
            );
        setSuccess(response.message || "Password changed successfully");
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else if (flow === "company_user") {
        await apiService.completeCompanyUserSetup(
          email,
          formData.currentPassword,
          formData.newPassword,
        );
        await loginCompanyUser({
          email,
          password: formData.newPassword,
        });
        // Redirect to company dashboard
        navigate("/company/dashboard", { replace: true });
      } else {
        await apiService.completeJobSeekerSetup(
          email,
          formData.currentPassword,
          formData.newPassword,
        );
        await loginJobSeeker({
          email,
          password: formData.newPassword,
        });
        // Redirect to candidate onboarding
        navigate("/candidate/onboarding?step=welcome", { replace: true });
      }
    } catch (err: any) {
      setError(
        getErrorMessage(err, "Failed to update password. Please try again."),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100">
            <svg
              className="h-6 w-6 text-indigo-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {isChangePasswordPage ? "Change Password" : "Set Your Password"}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {isChangePasswordPage
              ? "Update your account password"
              : "Create a secure password for your account"}
          </p>
          {!isChangePasswordPage && (
            <p className="text-sm font-medium text-indigo-600">{email}</p>
          )}
        </div>

        <div className="bg-white shadow-xl rounded-lg p-8">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative">
              <label
                htmlFor="currentPassword"
                className="block text-sm font-medium text-gray-700"
              >
                {isChangePasswordPage
                  ? "Current Password"
                  : "Current Temporary Password"}
              </label>
              <input
                id="currentPassword"
                name="currentPassword"
                required
                type={showCurrentPassword ? "password" : "text"}
                value={formData.currentPassword}
                onChange={handleChange}
                className={`mt-1 block w-full px-3 py-2 border ${
                  fieldErrors.currentPassword
                    ? "border-red-300"
                    : "border-gray-300"
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                placeholder={
                  isChangePasswordPage
                    ? "Enter your current password"
                    : "Enter your temporary password"
                }
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword((prev) => !prev)}
                className="absolute right-3 top-9 text-gray-500"
              >
                {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              {fieldErrors.currentPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {fieldErrors.currentPassword}
                </p>
              )}
            </div>

            <div className="relative">
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700"
              >
                New Password
              </label>

              <input
                id="newPassword"
                name="newPassword"
                type={showPassword ? "password" : "text"}
                required
                value={formData.newPassword}
                onChange={handleChange}
                className={`mt-1 block w-full px-3 py-2 pr-10 border ${
                  fieldErrors.newPassword ? "border-red-300" : "border-gray-300"
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                placeholder="Create a strong password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-gray-500"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>

              {fieldErrors.newPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {fieldErrors.newPassword}
                </p>
              )}

              <p className="mt-1 text-xs text-gray-500">
                Password must be at least 8 characters with uppercase,
                lowercase, number, and special character
              </p>
            </div>

            <div className="relative">
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "password" : "text"}
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`mt-1 block w-full px-3 py-2 border ${
                  fieldErrors.confirmPassword
                    ? "border-red-300"
                    : "border-gray-300"
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                placeholder="Confirm your new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute right-3 top-9 text-gray-500"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              {fieldErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {fieldErrors.confirmPassword}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 ease-in-out"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating Password...
                </div>
              ) : isChangePasswordPage ? (
                "Change Password"
              ) : (
                "Update Password"
              )}
            </button>
          </form>

          {!isChangePasswordPage && (
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                After updating your password, you'll need to log in again with
                your new credentials.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordSetupPage;
